import React, { useEffect, useState } from 'react';

// --- Configuration and Data Structure ---
// NOTE: Replace with your actual PayMe Public API Key from your merchant dashboard
// You can also use an environment variable: process.env.REACT_APP_PAYME_PUBLIC_KEY
const MERCHANT_API_KEY = process.env.REACT_APP_PAYME_SELLER_KEY || "YOUR_MERCHANT_API_KEY";
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";
console.log('merchant key:', MERCHANT_API_KEY);
/**
 * Example saleDetails structure expected by this component:
 * {
 * seller_payme_id: "MPL16148-77043XMH-T5MOWMZB-DDTAZOPP",
 * sale_price: "100.00",
 * currency: "EUR",
 * product_name: "Shirt",
 * sale_type: "sale",
 * sale_callback_url: "https://www.example.com/payment/3ds-callback",
 * payerFirstName: "John",
 * payerLastName: "Doe",
 * payerEmail: "john.doe@example.com",
 * // total structure for client-side tokenization
 * total: {
 * amount: { value: '100.00', currency: 'EUR' },
 * label: 'Shirt Purchase'
 * }
 * }
 */


// --- Helper Hook to load PayMe script ---
const usePayMeScript = () => {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (window.PayMe) {
      setLoaded(true);
      return;
    }
    const script = document.createElement('script');
    script.src = "https://cdn.payme.io/hf/v1/hostedfields.js";
    script.async = true;
    script.onload = () => setLoaded(true);
    document.body.appendChild(script);
    return () => document.body.removeChild(script);
  }, []);
  return loaded;
};


// --- The Digital Wallet Placeholder Component ---
const DigitalWalletButtons = ({ handleWalletPay }) => (
    <div className="digital-wallets" style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
      {/* NOTE: The logic for generating the secure token from the browser must be implemented in handleWalletPay */}
      <button 
          type="button" 
          onClick={() => handleWalletPay('apple-pay')}
          style={{ flex: 1, padding: '10px', background: 'black', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
      >
         Pay
      </button>
      <button 
          type="button" 
          onClick={() => handleWalletPay('google-pay')}
          style={{ flex: 1, padding: '10px', background: 'black', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
      >
        G Pay
      </button>
    </div>
);


// --- Main Checkout Component ---
const PayMe3DSCheckout = ({ saleDetails }) => {
  const scriptLoaded = usePayMeScript();
  const [paymeInstance, setPaymeInstance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');

  // 1. Initialize PayMe when script loads
  useEffect(() => {
    if (scriptLoaded && !paymeInstance) {
      window.PayMe.create(MERCHANT_API_KEY, { testMode: false }) // Set testMode: false in Prod
        .then((instance) => {
          setPaymeInstance(instance);
          const fields = instance.hostedFields();

          // Create & Mount Fields
          const options = { 
            styles: { base: { color: '#333', fontSize: '16px', height: '100%' } } 
          };
          
          // NOTE: .mount() expects a CSS selector STRING, not a DOM element
          fields.create(window.PayMe.fields.NUMBER, options).mount('#card-number-field');
          fields.create(window.PayMe.fields.EXPIRATION, options).mount('#card-expiration-field');
          fields.create(window.PayMe.fields.CVC, options).mount('#card-cvc-field');
        })
        .catch((err) => {
            console.error("PayMe Initialization Error:", err);
            setStatusMsg("Error initializing payment system.");
        });
    }
  }, [scriptLoaded, paymeInstance]);

  // --- NEW HANDLE PAY FUNCTION ---
  const handlePay = async (paymentData) => {
    setLoading(true);
    setStatusMsg('Generating sale...');

    try {
      // Step 1: Call /sale-generate to create the sale
      const saleGeneratePayload = {
        sale_price: paymentData.sale_price,
        currency: paymentData.currency || "ILS",
        product_name: paymentData.product_name,
        transaction_id: paymentData.transaction_id,
        sale_mobile: paymentData.sale_mobile,
        sale_name: paymentData.sale_name,
        sale_payment_method: paymentData.sale_payment_method || "credit-card",
        language: paymentData.language || "he",
      };

      const saleGenerateResponse = await fetch(`${API_URL}/sale-generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(saleGeneratePayload),
      });

      if (!saleGenerateResponse.ok) {
        throw new Error(`Sale generate failed: ${saleGenerateResponse.statusText}`);
      }

      const saleGenerateData = await saleGenerateResponse.json();

      if (saleGenerateData.status_code !== 0) {
        throw new Error(`Sale generate error: ${saleGenerateData.status_message || 'Unknown error'}`);
      }

      const paymeSaleId = saleGenerateData.payme_sale_id;
      if (!paymeSaleId) {
        throw new Error("No payme_sale_id returned from sale-generate");
      }

      setStatusMsg('Processing payment...');

      // Step 2: Call /pay-sale to complete the payment
      const paySalePayload = {
        sale_price: String(paymentData.sale_price),
        currency: paymentData.currency || "ILS",
        buyer_email: paymentData.buyer_email,
        buyer_name: paymentData.buyer_name || paymentData.sale_name,
        payme_sale_id: paymeSaleId,
        buyer_key : paymentData.card_token,
        // meta_data_jwt: paymentData.card_token, // Token from hosted fields
      };

      const paySaleResponse = await fetch(`${API_URL}/pay-sale`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(paySalePayload),
      });

      if (!paySaleResponse.ok) {
        throw new Error(`Pay sale failed: ${paySaleResponse.statusText}`);
      }

      const paySaleData = await paySaleResponse.json();

      if (paySaleData.payme_status === 'success' && paySaleData.status_error_code === 0) {
        setStatusMsg('Payment Successful!');
        return {
          success: true,
          data: paySaleData,
          saleId: paymeSaleId,
          saleCode: paySaleData.payme_sale_code,
        };
      } else {
        throw new Error(paySaleData.status_message || 'Payment failed');
      }

    } catch (error) {
      console.error('Payment error:', error);
      setStatusMsg(`Payment Failed: ${error.message}`);
      return {
        success: false,
        error: error.message,
      };
    } finally {
      setLoading(false);
    }
  };

  // --- DIGITAL WALLET HANDLER (Placeholder) ---
  const handleDigitalWalletPayment = (method) => {
    setStatusMsg(`Initiating ${method} flow...`);
    
    // 1. Use the browser's Payment Request API (or Apple/Google specific SDK) 
    //    to get the encrypted wallet token.

    // 2. Send the token and sale details to YOUR BACKEND:
    // fetch('/api/payme/generate-sale-wallet', {
    //    method: 'POST',
    //    body: JSON.stringify({ ...saleDetails, sale_payment_method: method, wallet_token: '...' })
    // });

    // 3. The backend handles the transaction and returns the result/redirect.
    alert(`Initiating ${method}. This typically uses the browser's native API to obtain a token, which is then sent to the backend's generate-sale endpoint.`);
    setLoading(false);
  };


  return (
    <div className="payme-checkout-container" style={{ maxWidth: '500px', margin: '0 auto', padding: '20px', border: '1px solid #ddd', borderRadius: '8px', position: 'relative', zIndex: 10 }}>
      {/* <h2>Secure 3D Checkout</h2> */}
      
      <DigitalWalletButtons handleWalletPay={handleDigitalWalletPayment} />

      <div style={{ textAlign: 'center', margin: '20px 0', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>
        <span style={{ backgroundColor: 'white', padding: '0 10px', color: '#555' }}>Or pay with Credit Card</span>
      </div>

      {/* Hosted Fields Form */}
      <form onSubmit={async (e) => {
        e.preventDefault();
        if (!paymeInstance) {
          setStatusMsg('Payment system not initialized');
          return;
        }
        
        setLoading(true);
        setStatusMsg('Tokenizing card data...');
        
        try {
    //         {

    //   payerFirstName: 'Foo',
    //   payerLastName: 'Bar',
    //   payerEmail: 'foo-bar@domain.com',
    //   payerPhone: '1231231',
    //   payerSocialId: '12345',
    //   payerZipCode: '123456',

    //   total: {
    //     label: 'Order #123123',
    //     amount: {
    //       currency: 'USD',
    //       value: '55.00',
    //     }
    //   }
    // };

{
    // "payerFirstName": "John",
    // "payerLastName": "Doe",
    // "payerEmail": "john.doe@example.com",
    // "total": {
    //     "amount": {
    //         "value": "5.00",
    //         "currency": "ILS"
    //     },
    //     "label": "Shirt Purchase"
    // },
    // "payerPhone": "+972501234567"
}

        const details = {
            payerFirstName: saleDetails.payerFirstName,
            payerLastName: saleDetails.payerLastName,
            payerEmail: saleDetails.payerEmail,
            total: saleDetails.total,
            payerPhone: saleDetails.payerPhone,
          }
          console.log('Tokenization details:', details);
          // Tokenize card data from hosted fields
          const tokenResult = await paymeInstance.tokenize({
            payerFirstName: saleDetails.payerFirstName,
            payerLastName: saleDetails.payerLastName,
            payerEmail: saleDetails.payerEmail,
            total: saleDetails.total,
            payerPhone: saleDetails.payerPhone,
            // "seller_payme_id": MERCHANT_API_KEY

          });
          console.log('Tokenization result:', tokenResult);
          
          // Call handlePay with tokenized card data
          await handlePay({
            sale_price: saleDetails.sale_price,
            currency: saleDetails.currency,
            product_name: saleDetails.product_name,
            transaction_id: saleDetails.transaction_id,
            sale_mobile: saleDetails.sale_mobile,
            sale_name: saleDetails.sale_name || `${saleDetails.payerFirstName} ${saleDetails.payerLastName}`,
            sale_payment_method: saleDetails.sale_payment_method || "credit-card",
            language: saleDetails.language || "he",
            buyer_email: saleDetails.payerEmail || saleDetails.buyer_email,
            buyer_name: saleDetails.buyer_name || saleDetails.sale_name,
            card_token: tokenResult.token, // Use the token from hosted fields
          });
        } catch (error) {
          console.error('Tokenization error:', error);
          setStatusMsg(`Error: ${error.message}`);
          setLoading(false);
        }
      }}>
        {/* Card Data Fields */}
        <div style={{ marginBottom: '15px', position: 'relative', zIndex: 10 }}>
          <label style={{ display: 'block', marginBottom: '5px' }}>CARD NUMBER</label>
          <div id="card-number-field" className="form-control" style={{ height: '40px', border: '1px solid #ccc', borderRadius: '4px', position: 'relative', zIndex: 10 }}></div>
        </div>
        <div style={{ display: 'flex', gap: '15px', marginBottom: '15px', position: 'relative', zIndex: 10 }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>EXPIRATION</label>
            <div id="card-expiration-field" className="form-control" style={{ height: '40px', border: '1px solid #ccc', borderRadius: '4px', position: 'relative', zIndex: 10 }}></div>
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>CVC</label>
            <div id="card-cvc-field" className="form-control" style={{ height: '40px', border: '1px solid #ccc', borderRadius: '4px', position: 'relative', zIndex: 10 }}></div>
          </div>
        </div>

        {/* Status and Button */}
        {statusMsg && <p style={{ color: loading ? '#007bff' : '#d35400', marginBottom: '15px' }}>{statusMsg}</p>}

        <button 
          type="submit" 
          disabled={loading || !paymeInstance}
          style={{ width: '100%', padding: '12px', background: '#2980b9', color: 'white', border: 'none', borderRadius: '4px', fontSize: '16px', cursor: loading ? 'not-allowed' : 'pointer' }}
        >
          {loading ? 'Processing...' : `Pay ${saleDetails.sale_price / 100} ${saleDetails.currency}`}
        </button>
      </form>
    </div>
  );
};

export default PayMe3DSCheckout;